// Pictures from Wikimedia for Look Up (SuiteImport/WikimediaPictures.swift; design-docs/Look Up — design.md §5): the
// Wikipedia article's own lead picture first, then Wikimedia Commons' search, each with its licence and author read
// from the file's `extmetadata`, so a picture the writer keeps carries its credit. The one web source Look Up has, asked
// only when the writer presses the button; the thumbnails are shown and forgotten until Keep downloads the file. The
// browser can't name the app in a User-Agent; Wikimedia's API answers the page with CORS when asked with `origin=*`.

export interface WikiPicture {
  /** The file's title on Commons ("File:Leicester Clock Tower.jpg"). */
  id: string;
  /** The file's object name, else its title without "File:" and the extension. */
  title: string;
  thumbnailURL: string;
  fileURL: string;
  /** The file's own page on Commons, which the credit names. */
  pageURL: string;
  /** Plain text; empty when the file doesn't say. */
  author: string;
  /** "CC BY-SA 4.0", "Public domain"; empty when the file doesn't say. */
  licence: string;
  attributionRequired: boolean;
  width: number;
  height: number;
  mimeType: string;
  /** The Wikipedia article's lead picture, shown first. */
  fromArticle: boolean;
}

export interface WikiPage { pictures: WikiPicture[]; nextOffset: number | null }

/** The largest file Keep downloads. */
export const FILE_LIMIT = 25 * 1024 * 1024;
export const THUMBNAIL_WIDTH = 640;

/** The line Keep writes into the page's notes, so the picture's source and licence stay with it. */
export const pictureCredit = (p: WikiPicture) =>
  `Picture: ${p.title} — ${p.author || 'author not named'}, ${p.licence || 'licence not stated'}, ${p.pageURL}`;

const isLanguageCode = (c: string) => c.length >= 2 && c.length <= 3 && /^[a-z]+$/.test(c);

/** `WikipediaSearch.language(forLocale:)`: the wiki for a project's locale ("en-GB" → "en", "nb-NO" → "no"). */
export function wikiLanguage(locale: string): string {
  let code = 'en';
  try { code = (new Intl.Locale(locale).language ?? 'en').toLowerCase(); } catch { /* unrecognised: English */ }
  const wiki = ({ nb: 'no', fil: 'tl' } as Record<string, string>)[code] ?? code;
  return isLanguageCode(wiki) ? wiki : 'en';
}

const IMAGE_INFO = {
  prop: 'imageinfo', iiprop: 'url|extmetadata|size|mime', iiurlwidth: String(THUMBNAIL_WIDTH),
  iiextmetadatafilter: 'LicenseShortName|Artist|AttributionRequired|ObjectName', format: 'json', formatversion: '2', origin: '*',
};

const api = (host: string, params: Record<string, string>) => `https://${host}/w/api.php?` + new URLSearchParams(params).toString();

export const commonsURL = (query: string, offset: number, limit: number) => api('commons.wikimedia.org', {
  action: 'query', generator: 'search', gsrsearch: 'filetype:bitmap ' + query, gsrnamespace: '6',
  gsrlimit: String(Math.min(Math.max(limit, 1), 50)), gsroffset: String(Math.max(offset, 0)), ...IMAGE_INFO,
});
export const fileInfoURL = (titles: string[]) => api('commons.wikimedia.org', { action: 'query', titles: titles.join('|'), ...IMAGE_INFO });
export const leadURL = (language: string, title: string) => api(`${language}.wikipedia.org`, {
  action: 'query', titles: title, prop: 'pageimages', piprop: 'name', redirects: '1', format: 'json', formatversion: '2', origin: '*',
});

export type Fetch = (url: string) => Promise<{ ok: boolean; status: number; json(): Promise<unknown>; arrayBuffer(): Promise<ArrayBuffer> }>;
const defaultFetch: Fetch = (url) => fetch(url, { headers: { Accept: 'application/json' } });

async function getJSON(url: string, fetchFn: Fetch): Promise<unknown> {
  const r = await fetchFn(url);
  if (!r.ok) throw new Error(`Wikimedia couldn't answer just now (it said ${r.status}).`);
  try { return await r.json(); } catch { throw new Error("Wikimedia's answer couldn't be read."); }
}

/** Pictures for the words: at offset 0 the article's lead picture (when Wikipedia has an article of that title), then
 *  Commons' bitmap search, `limit` at a time. A failure in the article step is passed over; Commons' is thrown. */
export async function fetchPictures(query: string, locale: string, offset = 0, limit = 20, fetchFn: Fetch = defaultFetch): Promise<WikiPage> {
  const words = query.trim();
  if (!words) return { pictures: [], nextOffset: null };
  let lead: WikiPicture | null = null;
  if (offset === 0) {
    try {
      const file = parseLeadFile(await getJSON(leadURL(wikiLanguage(locale), words), fetchFn));
      if (file) {
        const info = parseCommons(await getJSON(fileInfoURL(['File:' + file]), fetchFn)).pictures[0];
        if (info) lead = { ...info, fromArticle: true };
      }
    } catch { /* no article, or Wikipedia away: Commons alone */ }
  }
  const page = parseCommons(await getJSON(commonsURL(words, offset, limit), fetchFn));
  if (lead) page.pictures = [lead, ...page.pictures.filter((p) => p.id !== lead!.id)];
  return page;
}

/** The whole file, for Keep (at most `FILE_LIMIT`). */
export async function downloadPicture(picture: WikiPicture, fetchFn: Fetch = (url) => fetch(url)): Promise<Uint8Array> {
  const r = await fetchFn(picture.fileURL);
  if (!r.ok) throw new Error(`Wikimedia couldn't answer just now (it said ${r.status}).`);
  const bytes = new Uint8Array(await r.arrayBuffer());
  if (bytes.length > FILE_LIMIT) throw new Error('That picture is too large to keep (over 25 MB).');
  return bytes;
}

// MARK: The answers

/** `{"query":{"pages":[{"title":"Leicester","pageimage":"Leicester_Clock_Tower.jpg"}]}}`; a missing page or one with
 *  no picture gives null. */
export function parseLeadFile(json: unknown): string | null {
  const pages = (json as any)?.query?.pages;
  if (!Array.isArray(pages)) return null;
  const page = pages.find((p: any) => p?.missing !== true);
  return typeof page?.pageimage === 'string' ? page.pageimage : null;
}

/** Commons' `query.pages` (format version 2) with each file's `imageinfo`, in the search's order; files with no usable
 *  info (a missing thumbnail, a non-image) are left out. */
export function parseCommons(json: unknown): WikiPage {
  const query = (json as any)?.query;
  const pages: any[] = Array.isArray(query?.pages) ? [...query.pages] : [];
  pages.sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
  const pictures: WikiPicture[] = [];
  for (const page of pages) {
    const info = page?.imageinfo?.[0];
    if (!info || typeof page?.title !== 'string') continue;
    const thumb = info.thumburl, file = info.url, mime = info.mime;
    if (typeof thumb !== 'string' || typeof file !== 'string' || typeof mime !== 'string' || !mime.startsWith('image/')) continue;
    const meta = info.extmetadata ?? {};
    const value = (k: string) => plainText(typeof meta[k]?.value === 'string' ? meta[k].value : '');
    const named = value('ObjectName');
    pictures.push({
      id: page.title, title: named || fileName(page.title), thumbnailURL: thumb, fileURL: file,
      pageURL: typeof info.descriptionurl === 'string' ? info.descriptionurl : file,
      author: value('Artist'), licence: value('LicenseShortName'),
      attributionRequired: String(meta.AttributionRequired?.value ?? '').toLowerCase() === 'true',
      width: typeof info.width === 'number' ? info.width : 0, height: typeof info.height === 'number' ? info.height : 0,
      mimeType: mime, fromArticle: false,
    });
  }
  const next = (json as any)?.continue?.gsroffset;
  return { pictures, nextOffset: typeof next === 'number' ? next : null };
}

/** "File:Leicester_Clock_Tower.jpg" → "Leicester Clock Tower". */
export function fileName(title: string): string {
  let name = title;
  const colon = name.indexOf(':');
  if (colon > 0 && name.slice(0, colon).toLowerCase() === 'file') name = name.slice(colon + 1);
  const dot = name.lastIndexOf('.');
  if (dot > 0 && name.length - dot <= 5) name = name.slice(0, dot);
  return name.replaceAll('_', ' ').trim();
}

const ENTITIES: [string, string][] = [['&amp;', '&'], ['&lt;', '<'], ['&gt;', '>'], ['&quot;', '"'], ['&#39;', "'"], ['&nbsp;', ' ']];

/** Commons' metadata is HTML ("<a href=…>Jane Doe</a>", "Own work"): the words, with tags and entities gone, and a
 *  signature's talk-page link ("Name • talk") left off. */
export function plainText(html: string): string {
  let text = html.replace(/<[^>]+>/g, '');
  for (const [entity, char] of ENTITIES) text = text.replaceAll(entity, char);
  text = text.split(/\s+/).filter(Boolean).join(' ');
  for (const suffix of [' • talk', ' · talk', ' (talk)', ' | talk']) {
    if (text.toLowerCase().endsWith(suffix)) text = text.slice(0, -suffix.length);
  }
  return text.trim();
}

/** A picture as the apps keep photos (`PhotoImport`): at most 2,400 pixels on the long side, JPEG unless it has
 *  transparency (PNG). Without a canvas (a test) the bytes are kept as they are. */
export async function preparePhoto(bytes: Uint8Array, mimeType: string): Promise<{ bytes: Uint8Array; mimeType: string; extension: string }> {
  const asIs = { bytes, mimeType, extension: mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : mimeType === 'image/gif' ? 'gif' : 'jpg' };
  if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas !== 'function') return asIs;
  try {
    const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: mimeType }));
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > 2400 ? 2400 / longest : 1;
    const png = mimeType === 'image/png';
    if (scale === 1 && (png || mimeType === 'image/jpeg')) { bitmap.close(); return asIs; }
    const canvas = new OffscreenCanvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close(); return asIs; }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await canvas.convertToBlob(png ? { type: 'image/png' } : { type: 'image/jpeg', quality: 0.85 });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), mimeType: png ? 'image/png' : 'image/jpeg', extension: png ? 'png' : 'jpg' };
  } catch {
    return asIs;
  }
}
